/**
 * Configuration for revision diff display
 *
 * Defines which fields show full inline diff (with green/red highlighting)
 * versus which fields only show a "changed" indicator.
 */

export interface FieldConfig {
  /** Display name for the field */
  label: string
  /** Whether to show full text diff or just "changed" indicator */
  showFullDiff: boolean
  /** Field category for grouping in the UI */
  category: 'about' | 'personality' | 'instructions' | 'telephony' | 'integrations' | 'notifications' | 'admin'
}

/**
 * Field configuration for diff display
 *
 * Fields with showFullDiff: true will display side-by-side text comparison
 * Fields with showFullDiff: false will only show "Changed" indicator
 */
export const DIFF_FIELD_CONFIG: Record<string, FieldConfig> = {
  // About Tab
  name: {
    label: 'Name',
    showFullDiff: true,
    category: 'about',
  },
  'prompt_scenarios.about_business': {
    label: 'Business Description',
    showFullDiff: true,
    category: 'about',
  },
  customer_id: {
    label: 'Customer',
    showFullDiff: false,
    category: 'about',
  },
  timezone: {
    label: 'Timezone',
    showFullDiff: false,
    category: 'about',
  },
  'config.google_place_id': {
    label: 'Business Location',
    showFullDiff: false,
    category: 'about',
  },
  'config.show_opening_hours': {
    label: 'Show Opening Hours',
    showFullDiff: false,
    category: 'about',
  },
  'config.whatsapp_image': {
    label: 'WhatsApp Profile Image',
    showFullDiff: false,
    category: 'about',
  },

  // Personality Tab
  'prompt_scenarios.role': {
    label: 'Persona Role',
    showFullDiff: true,
    category: 'personality',
  },
  greeting: {
    label: 'Greeting',
    showFullDiff: true,
    category: 'personality',
  },
  voice_id: {
    label: 'Voice',
    showFullDiff: false,
    category: 'personality',
  },
  default_language: {
    label: 'Primary Language',
    showFullDiff: false,
    category: 'personality',
  },
  additional_languages: {
    label: 'Additional Languages',
    showFullDiff: false,
    category: 'personality',
  },
  lexicon: {
    label: 'Pronunciation/Lexicon',
    showFullDiff: false,
    category: 'personality',
  },
  'meta.greeting_mode': {
    label: 'Greeting Mode',
    showFullDiff: false,
    category: 'personality',
  },
  'meta.greeting_style': {
    label: 'Greeting Style',
    showFullDiff: false,
    category: 'personality',
  },

  // Instructions Tab
  prompt: {
    label: 'Main Instructions',
    showFullDiff: true,
    category: 'instructions',
  },
  post_call_prompt: {
    label: 'Post-Call Prompt',
    showFullDiff: true,
    category: 'instructions',
  },

  // FAQ Tab (part of instructions)
  'prompt_scenarios.faq': {
    label: 'FAQ',
    showFullDiff: true,
    category: 'instructions',
  },

  // Telephony Tab
  phone_number_id: {
    label: 'Phone Number',
    showFullDiff: false,
    category: 'telephony',
  },
  fallback_number: {
    label: 'Fallback Number',
    showFullDiff: false,
    category: 'telephony',
  },
  'config.recording_mode': {
    label: 'Recording Mode',
    showFullDiff: false,
    category: 'telephony',
  },
  'config.recording_notice': {
    label: 'Recording Notice',
    showFullDiff: false,
    category: 'telephony',
  },
  'config.transfer_flows': {
    label: 'Transfer Flows',
    showFullDiff: false,
    category: 'telephony',
  },
  'config.answer_schedule': {
    label: 'Answer Schedule',
    showFullDiff: false,
    category: 'telephony',
  },
  'prompt_scenarios.transfer_policy': {
    label: 'Transfer Policy',
    showFullDiff: true,
    category: 'telephony',
  },
  'prompt_scenarios.call_policy': {
    label: 'Call Policy',
    showFullDiff: true,
    category: 'admin',
  },

  // Integrations Tab
  'config.tools': {
    label: 'Integrations',
    showFullDiff: false,
    category: 'integrations',
  },

  // Notifications Tab
  notification_user: {
    label: 'Notification Recipient',
    showFullDiff: false,
    category: 'notifications',
  },
  notification_outcomes: {
    label: 'Notification Triggers',
    showFullDiff: false,
    category: 'notifications',
  },
  'config.include_transcript': {
    label: 'Include Transcript in Email',
    showFullDiff: false,
    category: 'notifications',
  },
  'config.daily_report': {
    label: 'Daily Report',
    showFullDiff: false,
    category: 'notifications',
  },

  // Admin Settings Tab
  ai_deployment_id: {
    label: 'AI Model',
    showFullDiff: false,
    category: 'admin',
  },
  'config.tts.use_human_time_format': {
    label: 'Human Time Format',
    showFullDiff: false,
    category: 'admin',
  },
  'config.stt.interruption': {
    label: 'Allow Interruption',
    showFullDiff: false,
    category: 'admin',
  },
  'config.stt.backend': {
    label: 'STT Backend',
    showFullDiff: false,
    category: 'admin',
  },
  config: {
    label: 'Configuration (JSON)',
    showFullDiff: false,
    category: 'admin',
  },
}

/**
 * Get category label for display (kept for backwards compatibility)
 */
export const CATEGORY_LABELS: Record<FieldConfig['category'], string> = {
  about: 'About',
  personality: 'Personality',
  instructions: 'Instructions & FAQ',
  telephony: 'Telephony',
  integrations: 'Integrations',
  notifications: 'Notifications',
  admin: 'Admin Settings',
}

/**
 * Map field paths to translation keys
 */
const FIELD_TRANSLATION_KEYS: Record<string, string> = {
  name: 'name',
  'prompt_scenarios.about_business': 'about_business',
  customer_id: 'customer',
  timezone: 'timezone',
  'config.google_place_id': 'business_location',
  'config.show_opening_hours': 'show_opening_hours',
  'config.whatsapp_image': 'whatsapp_image',
  'prompt_scenarios.role': 'persona_role',
  greeting: 'greeting',
  voice_id: 'voice',
  default_language: 'primary_language',
  additional_languages: 'additional_languages',
  lexicon: 'lexicon',
  'meta.greeting_mode': 'greeting_mode',
  'meta.greeting_style': 'greeting_style',
  prompt: 'main_instructions',
  post_call_prompt: 'post_call_prompt',
  'prompt_scenarios.faq': 'faq',
  phone_number_id: 'phone_number',
  fallback_number: 'fallback_number',
  'config.recording_mode': 'recording_mode',
  'config.recording_notice': 'recording_notice',
  'config.transfer_flows': 'transfer_flows',
  'config.answer_schedule': 'answer_schedule',
  'prompt_scenarios.transfer_policy': 'transfer_policy',
  'prompt_scenarios.call_policy': 'call_policy',
  'config.tools': 'integrations',
  notification_user: 'notification_recipient',
  notification_outcomes: 'notification_triggers',
  'config.include_transcript': 'include_transcript',
  'config.daily_report': 'daily_report',
  ai_deployment_id: 'ai_model',
  'config.tts.use_human_time_format': 'human_time_format',
  'config.stt.interruption': 'allow_interruption',
  'config.stt.backend': 'stt_backend',
  config: 'config_json',
}

/**
 * Get translated field label
 * Falls back to the static config label if no translation key is found
 */
export function getTranslatedFieldLabel(
  fieldPath: string,
  t: (key: string) => string
): string {
  const translationKey = FIELD_TRANSLATION_KEYS[fieldPath]
  if (translationKey) {
    return t(`resources.assistants.revisions.fields.${translationKey}`)
  }
  // Fall back to static config label
  const config = DIFF_FIELD_CONFIG[fieldPath]
  return config?.label || fieldPath
}

/**
 * Fields that should be excluded from diff display
 * (internal/system fields that aren't user-editable)
 */
export const EXCLUDED_FIELDS = [
  'id',
  'created_at',
  'updated_at',
  'deleted_at',
  'stripe_subscription',
  'category', // Assistant category (restaurant, etc.)
  'ai_model', // Old field, replaced by ai_deployment_id
  'from_id', // Internal WhatsApp field
  'email', // Deprecated field
  'formitable_id', // Legacy field
]
